"use client";

import React from "react";
import { Check } from "lucide-react";
import type { ContentCard } from "@/types/cms";
import styles from "./cardLesson.module.css";

export type LessonStatus = "done" | "current" | "todo";

export interface LessonItem {
  title: string;
  subtitle: string;
  status: LessonStatus;
}

export interface CardLessonProps {
  /** Panel heading. */
  title?: string;
  /** Lesson rows, top to bottom. Defaults to the Figma "Your Lesson Plan" set. */
  lessons?: LessonItem[];
  /** When true (default), statuses advance as the panel scrolls past the
   *  viewport's horizontal midline (empty → active → checked). Set false — or
   *  under prefers-reduced-motion — to render the static `lessons` statuses. */
  scrollDriven?: boolean;
  /** Accepted for render-pipeline parity with other card variants; the panel
   *  content is a static depiction, so it's unused. */
  card?: ContentCard;
}

/** Default rows — match Figma node 11726-183 ("Your Lesson Plan"). The `status`
 *  here is the static/SSR + reduced-motion fallback; live status is scroll-driven. */
export const DEFAULT_LESSONS: LessonItem[] = [
  { title: "The Basics", subtitle: "Learn how to get better responses from Ai.", status: "done" },
  { title: "Making Documents", subtitle: "Learn how Ai can write and edit documents.", status: "done" },
  { title: "Staying Safe", subtitle: "Learn what (and what not) to use Ai for.", status: "current" },
  { title: "Learning a New Skill", subtitle: "Use Ai to learn new skill or hobby.", status: "todo" },
  { title: "Advanced Prompting", subtitle: "Learn expert techniques for prompting Ai.", status: "todo" },
  { title: "Spot a Scam", subtitle: "Training to help you recognize modern scams.", status: "todo" },
  { title: "Plan a Project", subtitle: "Use Ai to scope and sequence a project.", status: "todo" },
];

/** Check + dot are always mounted so they can fade/scale between states; CSS
 *  reveals the right one based on the parent status class. */
function StatusCircle({ status }: { status: LessonStatus }) {
  return (
    <div className={`${styles.status} ${styles[status]}`}>
      <Check className={styles.check} strokeWidth={2.5} aria-hidden />
      <span className={styles.dot} aria-hidden />
    </div>
  );
}

/**
 * "Your Lesson Plan" panel — a code replica of the app's lessons UI (Figma
 * 11726-183). Statuses are scroll-driven: each lesson advances empty → active →
 * checked as that row's vertical middle crosses the horizontal line through the
 * middle of the viewport.
 */
export default function CardLesson({
  title = "Your Lesson Plan",
  lessons = DEFAULT_LESSONS,
  scrollDriven = true,
}: CardLessonProps) {
  const rowRefs = React.useRef<(HTMLLIElement | null)[]>([]);
  const [statuses, setStatuses] = React.useState<LessonStatus[]>(() =>
    lessons.map((l) => l.status),
  );

  React.useEffect(() => {
    const staticStatuses = lessons.map((l) => l.status);
    const reduceMotion =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (!scrollDriven || reduceMotion) {
      // One-shot sync to the static prop statuses when scroll-driving is off
      // (or reduced motion); not a render-derived value.
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional one-shot reset to static statuses
      setStatuses(staticStatuses);
      return;
    }

    let raf = 0;

    const compute = () => {
      raf = 0;
      const rows = rowRefs.current;
      const lineY = window.innerHeight / 2; // horizontal line through the middle
      const centers = rows.map((el) => {
        if (!el) return null;
        const r = el.getBoundingClientRect();
        return r.top + r.height / 2;
      });

      // Band ≈ one row pitch (distance between consecutive row centers). A row is
      // only "current" once it's within a band below the line, so the section
      // reads as empty before it's reached and fully checked after it's passed.
      const valid = centers.filter((c): c is number => c != null);
      let band = valid.length >= 2 ? Math.abs(valid[1] - valid[0]) : 0;
      if (!band) {
        const firstEl = rows.find(Boolean);
        band = firstEl ? firstEl.getBoundingClientRect().height || 72 : 72;
      }

      // Current = the below-line row closest to the line (the next to cross).
      let currentIdx = -1;
      let currentDist = Infinity;
      centers.forEach((c, i) => {
        if (c == null || c < lineY) return;
        const d = c - lineY;
        if (d < currentDist) {
          currentDist = d;
          currentIdx = i;
        }
      });

      const next: LessonStatus[] = centers.map((c, i) => {
        if (c == null) return "todo";
        if (c < lineY) return "done"; // middle passed above the line → checked
        if (i === currentIdx && currentDist <= band) return "current";
        return "todo";
      });

      setStatuses((prev) =>
        prev.length === next.length && prev.every((s, i) => s === next[i]) ? prev : next,
      );
    };

    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(compute);
    };

    compute();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [scrollDriven, lessons]);

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <p className={styles.headerTitle}>{title}</p>
      </div>
      <div className={styles.divider} />
      <ul className={styles.list}>
        {lessons.map((lesson, i) => (
          <li
            className={styles.row}
            key={`${lesson.title}-${i}`}
            ref={(el) => {
              rowRefs.current[i] = el;
            }}
          >
            <StatusCircle status={statuses[i] ?? lesson.status} />
            <div className={styles.content}>
              <p className={styles.lessonTitle}>{lesson.title}</p>
              <p className={styles.lessonSubtitle}>{lesson.subtitle}</p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
